// apps/web/src/lib/estoque/movimentacao.ts — Fase 13, Plano 11 (EST-09, EST-14, EST-16, D-06, D-07, D-09).
// Regras PURAS da Sheet "Nova movimentação": interpretação da quantidade, validação por tipo (mensagens do UI-SPEC) e
// montagem do payload/rota das três operações. A autorização e a checagem real de saldo são do servidor; aqui só se evita
// o round-trip óbvio (T-13-11A: sem adivinhar milhar, no máximo 4 casas).
import { MOTIVOS_PERDA, interpretarNumeroPtBr, mensagemNumeroAmbiguo } from '@pieltec/contracts';
import type { MotivoPerda, RegistrarConsumivelInput, RegistrarPerdaInput, TransferirProprioInput } from '@pieltec/contracts';
import { formatarQuantidadeEstoque } from './formatacao';

export const TIPOS_MOVIMENTACAO = ['TRANSFERENCIA', 'PERDA', 'CONSUMIVEL'] as const;
export type TipoMovimentacao = (typeof TIPOS_MOVIMENTACAO)[number];

export const ROTULOS_TIPO_MOVIMENTACAO: Readonly<Record<TipoMovimentacao, string>> = {
  TRANSFERENCIA: 'Transferir entre localizações',
  PERDA: 'Registrar perda ou sucata',
  CONSUMIVEL: 'Entregar consumível ao setor',
};

export const PERMISSAO_MOVIMENTAR_ESTOQUE = 'estoque:movimentar';
export const MENSAGEM_FALHA_MOVIMENTACAO = 'Não foi possível registrar a movimentação. Tente novamente.';
export const LIMITE_TEXTO_MOVIMENTACAO = 500;
export const CASAS_QUANTIDADE_MOVIMENTACAO = 4;

export const MENSAGEM_ESCOLHA_DESTINO = 'Escolha a localização de destino.';
export const MENSAGEM_ORIGEM_IGUAL_DESTINO = 'Origem e destino não podem ser iguais.';
export const MENSAGEM_INFORME_MOTIVO = 'Informe o motivo.';
const MENSAGEM_ESCOLHA_ITEM = 'Escolha o item.';
const MENSAGEM_ESCOLHA_ORIGEM = 'Escolha a localização de origem.';
const MENSAGEM_ESCOLHA_LOCALIZACAO = 'Escolha a localização.';
const MENSAGEM_ESCOLHA_CELULA = 'Escolha o setor / célula.';
const MENSAGEM_CASAS = `Use no máximo ${CASAS_QUANTIDADE_MOVIMENTACAO} casas decimais.`;
const MENSAGEM_TEXTO_LONGO = `Use no máximo ${LIMITE_TEXTO_MOVIMENTACAO} caracteres.`;

export type CampoMovimentacao =
  | 'materialId'
  | 'de'
  | 'localizacaoId'
  | 'quantidade'
  | 'para'
  | 'motivoPerda'
  | 'detalhes'
  | 'celulaId'
  | 'motivo'
  | 'observacao';

/** Ordem visual dos campos: o primeiro com erro recebe o foco. */
const ORDEM_CAMPOS: readonly CampoMovimentacao[] = [
  'materialId',
  'de',
  'localizacaoId',
  'quantidade',
  'para',
  'motivoPerda',
  'detalhes',
  'celulaId',
  'motivo',
  'observacao',
];

export interface ResultadoValidacaoMovimentacao {
  readonly erros: Partial<Record<CampoMovimentacao, string>>;
  /** Primeiro campo inválido na ordem visual; `null` quando tudo está válido. */
  readonly primeiroInvalido: CampoMovimentacao | null;
}

function resultado(erros: Partial<Record<CampoMovimentacao, string>>): ResultadoValidacaoMovimentacao {
  const primeiroInvalido = ORDEM_CAMPOS.find((campo) => erros[campo] !== undefined) ?? null;
  return { erros, primeiroInvalido };
}

export type QuantidadeInterpretada = { readonly tipo: 'ok'; readonly valor: number } | { readonly tipo: 'erro'; readonly mensagem: string };

function mensagemQuantidade(saldo: number | null, unidade: string): string {
  return saldo === null
    ? 'Informe uma quantidade maior que zero.'
    : `Informe uma quantidade maior que zero e no máximo ${formatarQuantidadeEstoque(saldo, unidade)}.`;
}

/**
 * Texto digitado -> quantidade. Vírgula ou ponto decimal; "1.234" (ambíguo) é erro; mais de 4 casas é erro.
 * `saldo` ausente (ainda não carregado) valida só `> 0`: o servidor revalida o saldo.
 */
export function interpretarQuantidade(texto: string, saldo: number | null, unidade: string): QuantidadeInterpretada {
  const lido = interpretarNumeroPtBr(texto);
  if (lido.tipo === 'vazio' || lido.tipo === 'invalido') return { tipo: 'erro', mensagem: mensagemQuantidade(saldo, unidade) };
  if (lido.tipo === 'ambiguo') return { tipo: 'erro', mensagem: mensagemNumeroAmbiguo(texto, lido) };
  const valor = lido.valor;
  if (!(valor > 0)) return { tipo: 'erro', mensagem: mensagemQuantidade(saldo, unidade) };
  const escalado = valor * 10 ** CASAS_QUANTIDADE_MOVIMENTACAO;
  if (Math.abs(escalado - Math.round(escalado)) > 1e-6) return { tipo: 'erro', mensagem: MENSAGEM_CASAS };
  if (saldo !== null && valor > saldo + 1e-9) return { tipo: 'erro', mensagem: mensagemQuantidade(saldo, unidade) };
  return { tipo: 'ok', valor };
}

export interface EntradaTransferencia {
  readonly materialId: string;
  readonly quantidade: string;
  readonly saldo: number | null;
  readonly unidade: string;
  readonly de: string;
  readonly para: string;
  readonly motivo?: string;
}

export function validarTransferencia(entrada: EntradaTransferencia): ResultadoValidacaoMovimentacao {
  const erros: Partial<Record<CampoMovimentacao, string>> = {};
  if (entrada.materialId === '') erros.materialId = MENSAGEM_ESCOLHA_ITEM;
  if (entrada.de === '') erros.de = MENSAGEM_ESCOLHA_ORIGEM;
  const quantidade = interpretarQuantidade(entrada.quantidade, entrada.saldo, entrada.unidade);
  if (quantidade.tipo === 'erro') erros.quantidade = quantidade.mensagem;
  if (entrada.para === '') erros.para = MENSAGEM_ESCOLHA_DESTINO;
  else if (entrada.para === entrada.de) erros.para = MENSAGEM_ORIGEM_IGUAL_DESTINO;
  if ((entrada.motivo ?? '').trim().length > LIMITE_TEXTO_MOVIMENTACAO) erros.motivo = MENSAGEM_TEXTO_LONGO;
  return resultado(erros);
}

export interface EntradaPerda {
  readonly materialId: string;
  readonly localizacaoId: string;
  readonly quantidade: string;
  readonly saldo: number | null;
  readonly unidade: string;
  readonly motivoPerda: MotivoPerda | '';
  readonly detalhes: string;
  /** "Lote ou cor (opcional)": referência livre, anexada aos detalhes. */
  readonly referencia?: string;
}

/** Detalhes enviados: o texto digitado e, se houver, a referência de lote/cor (D-06: sem lookup de lote, vira texto). */
export function detalhesDaPerda(detalhes: string, referencia: string | undefined): string {
  const texto = detalhes.trim();
  const ref = (referencia ?? '').trim();
  if (ref === '') return texto;
  return texto === '' ? `Lote/cor: ${ref}` : `${texto} — Lote/cor: ${ref}`;
}

export function validarPerda(entrada: EntradaPerda): ResultadoValidacaoMovimentacao {
  const erros: Partial<Record<CampoMovimentacao, string>> = {};
  if (entrada.materialId === '') erros.materialId = MENSAGEM_ESCOLHA_ITEM;
  if (entrada.localizacaoId === '') erros.localizacaoId = MENSAGEM_ESCOLHA_LOCALIZACAO;
  const quantidade = interpretarQuantidade(entrada.quantidade, entrada.saldo, entrada.unidade);
  if (quantidade.tipo === 'erro') erros.quantidade = quantidade.mensagem;
  if (entrada.motivoPerda === '') erros.motivoPerda = MENSAGEM_INFORME_MOTIVO;
  if (entrada.motivoPerda === 'OUTRO' && entrada.detalhes.trim() === '') erros.detalhes = MENSAGEM_INFORME_MOTIVO;
  else if (detalhesDaPerda(entrada.detalhes, entrada.referencia).length > LIMITE_TEXTO_MOVIMENTACAO) erros.detalhes = MENSAGEM_TEXTO_LONGO;
  return resultado(erros);
}

export interface EntradaConsumivel {
  readonly materialId: string;
  readonly quantidade: string;
  readonly saldo: number | null;
  readonly unidade: string;
  readonly celulaId: string;
  readonly observacao?: string;
}

export function validarConsumivel(entrada: EntradaConsumivel): ResultadoValidacaoMovimentacao {
  const erros: Partial<Record<CampoMovimentacao, string>> = {};
  if (entrada.materialId === '') erros.materialId = MENSAGEM_ESCOLHA_ITEM;
  const quantidade = interpretarQuantidade(entrada.quantidade, entrada.saldo, entrada.unidade);
  if (quantidade.tipo === 'erro') erros.quantidade = quantidade.mensagem;
  if (entrada.celulaId === '') erros.celulaId = MENSAGEM_ESCOLHA_CELULA;
  if ((entrada.observacao ?? '').trim().length > LIMITE_TEXTO_MOVIMENTACAO) erros.observacao = MENSAGEM_TEXTO_LONGO;
  return resultado(erros);
}

/** Campos digitados da Sheet (um conjunto só; cada tipo usa os seus). */
export interface CamposMovimentacao {
  readonly materialId: string;
  readonly unidade: string;
  readonly quantidade: string;
  readonly saldo: number | null;
  readonly de: string;
  readonly para: string;
  readonly localizacaoId: string;
  readonly motivo: string;
  readonly motivoPerda: MotivoPerda | '';
  readonly detalhes: string;
  readonly referencia: string;
  readonly celulaId: string;
  readonly observacao: string;
}

export function camposVazios(): CamposMovimentacao {
  return {
    materialId: '',
    unidade: '',
    quantidade: '',
    saldo: null,
    de: '',
    para: '',
    localizacaoId: '',
    motivo: '',
    motivoPerda: '',
    detalhes: '',
    referencia: '',
    celulaId: '',
    observacao: '',
  };
}

/** Valida os campos do tipo escolhido. */
export function validarMovimentacao(tipo: TipoMovimentacao, campos: CamposMovimentacao): ResultadoValidacaoMovimentacao {
  switch (tipo) {
    case 'TRANSFERENCIA':
      return validarTransferencia({ ...campos, motivo: campos.motivo });
    case 'PERDA':
      return validarPerda({ ...campos, referencia: campos.referencia });
    case 'CONSUMIVEL':
      return validarConsumivel({ ...campos, observacao: campos.observacao });
  }
}

export const ROTAS_MOVIMENTACAO: Readonly<Record<TipoMovimentacao, string>> = {
  TRANSFERENCIA: '/api/v1/estoque/movimentos-proprios/transferencias',
  PERDA: '/api/v1/estoque/movimentos-proprios/perdas',
  CONSUMIVEL: '/api/v1/estoque/movimentos-proprios/consumiveis',
};

export interface PayloadMovimentacao {
  readonly rota: string;
  readonly corpo: TransferirProprioInput | RegistrarPerdaInput | RegistrarConsumivelInput;
}

/** Tentativa de envio de uma movimentação manual: a chave vale para este conteúdo (rota + corpo) e só para ele. */
export interface TentativaMovimentacao {
  readonly assinatura: string;
  readonly chave: string;
}

/**
 * Chave de idempotência por tentativa (WR-C01): gerada uma vez por envio, reaproveitada no reenvio do MESMO conteúdo (rede
 * caída, 5xx, resposta perdida) e renovada quando qualquer campo do payload muda. Quem chama zera a tentativa após o sucesso.
 */
export function tentativaDaMovimentacao(
  atual: TentativaMovimentacao | null,
  payload: PayloadMovimentacao,
  gerar: () => string,
): TentativaMovimentacao {
  const assinatura = `${payload.rota}|${JSON.stringify(payload.corpo)}`;
  return atual !== null && atual.assinatura === assinatura ? atual : { assinatura, chave: gerar() };
}

/** Mesmo payload com a chave de idempotência da tentativa (transferência, perda e consumível aceitam o campo). */
export function comChaveDaTentativa(payload: PayloadMovimentacao, tentativa: TentativaMovimentacao): PayloadMovimentacao {
  return { rota: payload.rota, corpo: { ...payload.corpo, chaveIdempotencia: tentativa.chave } };
}

function textoOuNulo(texto: string): string | null {
  const limpo = texto.trim();
  return limpo === '' ? null : limpo;
}

/**
 * Rota e corpo da operação. Pressupõe campos já validados (`validarMovimentacao`); uma quantidade inválida lança erro
 * em vez de enviar lixo. Consumível não envia localização: o servidor usa o estoque preferencial do material (D-07).
 */
export function montarPayload(tipo: TipoMovimentacao, campos: CamposMovimentacao): PayloadMovimentacao {
  const quantidade = interpretarQuantidade(campos.quantidade, null, campos.unidade);
  if (quantidade.tipo === 'erro') throw new Error(quantidade.mensagem);
  const rota = ROTAS_MOVIMENTACAO[tipo];
  switch (tipo) {
    case 'TRANSFERENCIA':
      return {
        rota,
        corpo: {
          materialId: campos.materialId,
          deLocalizacaoId: campos.de,
          paraLocalizacaoId: campos.para,
          quantidade: quantidade.valor,
          motivo: textoOuNulo(campos.motivo),
        },
      };
    case 'PERDA': {
      if (campos.motivoPerda === '' || !MOTIVOS_PERDA.includes(campos.motivoPerda)) throw new Error(MENSAGEM_INFORME_MOTIVO);
      return {
        rota,
        corpo: {
          materialId: campos.materialId,
          localizacaoId: campos.localizacaoId,
          quantidade: quantidade.valor,
          motivoPerda: campos.motivoPerda,
          detalhes: textoOuNulo(detalhesDaPerda(campos.detalhes, campos.referencia)),
        },
      };
    }
    case 'CONSUMIVEL':
      return {
        rota,
        corpo: {
          materialId: campos.materialId,
          celulaId: campos.celulaId,
          quantidade: quantidade.valor,
          observacao: textoOuNulo(campos.observacao),
        },
      };
  }
}

/** "Saldo disponível em {local}: {qtd} {un}." (ajuda do campo Quantidade da transferência). */
export function ajudaSaldoDisponivel(localNome: string, saldo: number | null, unidade: string): string | null {
  if (saldo === null) return null;
  return `Saldo disponível em ${localNome}: ${formatarQuantidadeEstoque(saldo, unidade)}.`;
}

/** Saldo do local num conjunto de linhas de saldo do material; sem linha = 0 (o local não tem saldo). */
export function saldoNoLocal(linhas: readonly { localizacaoId: string; saldo: number }[] | null, localizacaoId: string): number | null {
  if (linhas === null || localizacaoId === '') return null;
  return linhas.find((linha) => linha.localizacaoId === localizacaoId)?.saldo ?? 0;
}

/** Saldo do consumível: o do estoque preferencial do material (padrão do servidor); sem linha preferencial = desconhecido. */
export function saldoPreferencial(linhas: readonly { preferencial: boolean; saldo: number }[] | null): number | null {
  if (linhas === null) return null;
  return linhas.find((linha) => linha.preferencial)?.saldo ?? null;
}
